import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, AxiosHeaders } from 'axios';
import {
  CHATBOT_ADD_FEEDBACK,
  CHATBOT_GET_TRACE_ID,
  CHATBOT_PROMPT_ENDPOINT,
  CHATBOT_RETRIEVE_SESSION_ENDPOINT,
} from 'config/endpoints';
import { get, post } from 'utils/axios';
import {
  ChatbotPromptParams,
  ChatbotPromptParamsDemo,
  ChatbotPromptResponse,
  ChatbotPromptResponseDemo,
  ChatbotSessionHistoryParams,
  ChatbotSessionHistoryResponse,
  FeedbackParams,
  FeedbackResponse,
  TracesResponse,
} from './contract';
import { buildChatMessage } from 'ui/LangFlowChat/langflow.helper';
import { useTranslation } from 'react-i18next';
import { GenericQueryOptions } from 'queries/events/types';
import { Message } from 'ui/LangFlowChat/Hooks/useLangflowChat';
import { useLocation, useParams } from 'react-router-dom';
import { IS_DEMO, LOCAL_STORAGE_WORKSPACE_ID } from 'config/constants';
import { getInfoByCurrentUrl } from './helper';
import { AppContext } from 'appContext';
import { useContext } from 'react';

const CHAT_TIMEOUT = 60000;

export const useChatbotPrompt = ({
  onSuccessHandler,
  onErrorHandler,
}: {
  onSuccessHandler: (message: Message, sessionId: string) => void;
  onErrorHandler: (error: Message) => void;
}) => {
  const location = useLocation();
  const routeParams = useParams();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const pageInfos = getInfoByCurrentUrl(location, queryClient, routeParams);
  const pageInfosUrl = new URLSearchParams(pageInfos).toString();

  const urlSeparator = location.search ? '&' : '?';
  const currentUrl = `${location.pathname}${location.search}${
    pageInfosUrl ? urlSeparator + pageInfosUrl : ''
  }`;
  const apiKey = import.meta.env.VITE_LANGFLOW_API_KEY;
  const {
    state: { auth },
  } = useContext(AppContext);
  return useMutation<
    { sessionId: string; response: string },
    AxiosError,
    Omit<ChatbotPromptParams, 'current_url'>
  >({
    mutationKey: ['LANGFLOW_PROMPT'],
    mutationFn: async ({ user_message, session_id }) => {
      const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID);

      if (IS_DEMO) {
        const headers = new AxiosHeaders();
        headers.set('x-api-key', apiKey);
        const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID);

        const response = await post<ChatbotPromptParamsDemo, ChatbotPromptResponseDemo>(
          CHATBOT_PROMPT_ENDPOINT(workspaceId ?? ''),
          {
            payload: {
              input_value: user_message,
              input_type: 'chat',
              output_type: 'chat',
              session_id,
              tweaks: {
                Prompt: {
                  'current-url': currentUrl,
                  'user-name': auth?.user?.name ?? '',
                  'user-email': auth?.user?.email ?? '',
                },
              },
              lh_workspace_id: workspaceId,
              lh_session_token: auth?.token?.replace('Bearer ', ''),
            },
            headers,

            requestConfig: { timeout: CHAT_TIMEOUT },
          },
        );

        return {
          sessionId: response.data.session_id,
          response: response.data.outputs?.[0]?.outputs?.[0]?.results?.message?.text,
        };
      }

      const response = await post<ChatbotPromptParams, ChatbotPromptResponse>(
        CHATBOT_PROMPT_ENDPOINT(workspaceId ?? ''),
        {
          payload: {
            user_message,
            current_url: currentUrl,
            session_id,
          },
          requestConfig: { timeout: CHAT_TIMEOUT },
        },
      );

      return {
        sessionId: response.data.session_id,
        response: response.data.chatbot_message,
      };
    },
    onSuccess: (data) => {
      onSuccessHandler(
        buildChatMessage({
          content: data.response,
          sender: 'Machine',
        }),
        data.sessionId,
      );
    },
    onError: (error) => {
      console.error('Error:', error.message);
      onErrorHandler(
        buildChatMessage({
          content:
            error.code === 'ECONNABORTED' ? t('chat:messages.timeout') : t('chat:messages.error'),
          sender: 'Machine',
        }),
      );
    },
  });
};

export const useChatbotSession = (
  sessionId: string,
  options?: GenericQueryOptions<ChatbotSessionHistoryResponse>,
) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID) ?? '';

  return useQuery(
    ['LANGFLOW_SESSION', sessionId],
    get<ChatbotSessionHistoryResponse, ChatbotSessionHistoryParams>(
      CHATBOT_RETRIEVE_SESSION_ENDPOINT(workspaceId, sessionId),
    ),
    { ...options },
  );
};

export const useGetTraceId = (sessionId: string) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID);
  if (!workspaceId) throw new Error('Workspace ID is required to fetch trace ID');
  return useQuery(
    ['LANGFLOW_TRACE_ID', sessionId],
    get<TracesResponse, { session_id: string }>(CHATBOT_GET_TRACE_ID(sessionId, workspaceId)),
    { enabled: !!sessionId },
  );
};

export const useFeedbackMutation = (traceId: string) => {
  const workspaceId = localStorage.getItem(LOCAL_STORAGE_WORKSPACE_ID);
  if (!workspaceId) throw new Error('Workspace ID is required to send feedback');

  return useMutation({
    mutationKey: ['LANGFLOW_FEEDBACK', traceId],
    mutationFn: async (value: boolean) => {
      const response = await post<FeedbackParams, FeedbackResponse>(
        CHATBOT_ADD_FEEDBACK(traceId, workspaceId),
        { payload: { value } },
      );
      return response.data;
    },
  });
};
